import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Users,
  HandHeart,
  Footprints,
  ShieldCheck,
  Building2,
  Globe,
  Quote,
  Sparkles,
  HeartHandshake,
  Activity,
  PhoneCall,
} from "lucide-react";
import { motion, useInView, animate } from "framer-motion";
import { LANDING } from "../constants/testIds";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BtcLogo from "../components/BtcLogo";

const HERO_IMAGE = "/img/hero.jpg";

// ---------------- Hero ----------------
function Hero() {
  return (
    <section className="relative pt-20 lg:pt-24 pb-0 bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[45%_55%] gap-10 lg:gap-14 items-center min-h-[640px] lg:min-h-[720px]">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="pt-6 lg:pt-4 pb-10 lg:pb-16"
          >
            <BtcLogo className="h-40 sm:h-52 md:h-60 lg:h-64 xl:h-72 w-auto -ml-1 mb-3 block" />
            <p
              data-testid={LANDING.heroEyebrow}
              className="text-[#23803F] text-[12.5px] font-bold uppercase tracking-[0.22em] mt-2 mb-5"
            >
              THE FIRST OF ITS KIND EVER
            </p>
            <h1
              data-testid={LANDING.heroHeadline}
              className="font-display font-extrabold tracking-tight leading-[1.02] text-[44px] sm:text-[56px] lg:text-[64px] xl:text-[72px] mb-5"
            >
              <span className="text-[#0B132B] block">Patient Portal Ecosystem</span>
              <span className="text-[#1A5FAB] block">Built Entirely for You.</span>
            </h1>
            <div className="h-[3px] w-16 bg-[#2D9B4E] rounded-full mb-6" />
            <p
              data-testid={LANDING.heroSupporting}
              className="text-[#23803F] font-bold text-[15px] sm:text-base uppercase tracking-[0.18em] mb-6"
            >
              NO ONE FACES CANCER ALONE
            </p>
            <p
              data-testid={LANDING.heroBody}
              className="text-[#4B5563] text-base sm:text-[17px] leading-relaxed max-w-[560px] mb-10"
            >
              Founded in the United Kingdom. Built for patients everywhere.
              A global community connecting patients, survivors, carers, volunteers, hospital trusts
              and support services to the resources, specialist support and hope you need — every step
              of your cancer journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/support"
                data-testid={LANDING.heroPrimaryCta}
                className="btc-btn-primary justify-center"
              >
                GET SUPPORT
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/portal-preview"
                data-testid={LANDING.heroSecondaryCta}
                className="btc-btn-outline justify-center"
              >
                EXPLORE THE PORTAL
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Right: hero image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
            className="relative h-[460px] sm:h-[560px] lg:h-[680px] rounded-3xl overflow-hidden"
          >
            <img
              data-testid={LANDING.heroImage}
              src={HERO_IMAGE}
              alt="A diverse cancer patient at home in a warm telehealth video consultation with a Bonded Thru Cancer intake specialist visible on her laptop — personalised guidance and support"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/25 via-black/0 to-transparent" />
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              data-testid={LANDING.heroFloatingCard}
              className="btc-glass absolute bottom-6 right-6 lg:bottom-10 lg:right-10 rounded-2xl px-5 py-4 lg:px-6 lg:py-5 flex items-start gap-3 max-w-[360px]"
            >
              <div className="shrink-0 w-10 h-10 rounded-full bg-[#2D9B4E] flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" fill="white" />
              </div>
              <div>
                <p className="text-white font-semibold text-[15px] leading-snug">
                  A Global Community.
                  <br />
                  United by Strength. Driven by Hope.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ---------------- What BTC Is (Mission Strip) ----------------
function WhatBTCIs() {
  const items = [
    { id: "missionConnect", icon: Users, title: "WE CONNECT", desc: "We connect people with others who truly understand." },
    { id: "missionSupport", icon: HandHeart, title: "WE SUPPORT", desc: "Practical help, resources and specialist support." },
    { id: "missionWalk", icon: Footprints, title: "WE WALK TOGETHER", desc: "We walk beside you through every step of your cancer journey." },
    { id: "missionHere", icon: ShieldCheck, title: "WE'RE HERE", desc: "A safe space. A trusted place. Always by your side." },
  ];
  return (
    <section data-testid={LANDING.mission} className="bg-[#1F5D5B] text-white">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 py-14 lg:py-16">
        <div className="max-w-2xl mb-10">
          <p className="text-[#8CD9AE] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            WHAT BTC IS
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
            A community that shows up.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10 divide-y sm:divide-y-0 sm:divide-x divide-white/15">
          {items.map((it, i) => (
            <motion.div
              key={it.id}
              data-testid={LANDING[it.id]}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className={`flex items-start gap-5 ${i > 0 ? "sm:pl-8" : ""} pt-8 sm:pt-0`}
            >
              <div className="shrink-0 w-14 h-14 rounded-full border-2 border-white/70 flex items-center justify-center">
                <it.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-bold text-[15px] tracking-[0.06em] mb-2">{it.title}</h3>
                <p className="text-white/85 text-[14px] leading-relaxed">{it.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/about" className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:gap-3 transition-all">
            Learn more about BTC <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---------------- Counter ----------------
function Counter({ to, suffix = "", prefix = "", duration = 1.4 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);
  const formatted = value >= 1000 ? value.toLocaleString() : value.toString();
  return (
    <span ref={ref} className="counter-num">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

// ---------------- How BTC Helps (Impact) ----------------
function HowBTCHelps() {
  const stats = [
    { id: "people", icon: Users, value: 120, suffix: "K+", label: "People Supported", color: "#2D9B4E" },
    { id: "vols", icon: HandHeart, value: 9500, suffix: "+", label: "Trained Volunteers", color: "#1A5FAB" },
    { id: "hosp", icon: Building2, value: 320, suffix: "+", label: "Hospital Trust Partners", color: "#2D9B4E" },
    { id: "country", icon: Globe, value: 12, suffix: "", label: "Countries. One Mission.", color: "#1A5FAB" },
  ];
  return (
    <section data-testid={LANDING.impact} className="bg-white border-b border-gray-100">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20">
        <div className="max-w-2xl mb-10">
          <p className="btc-eyebrow mb-3">HOW BTC HELPS</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0B132B] tracking-tight leading-tight">
            Support that reaches the people who need it.
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-2">
          {stats.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={LANDING.impactStat}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              className={`flex items-center gap-4 ${i > 0 ? "lg:pl-6 lg:border-l lg:border-gray-200" : ""}`}
            >
              <div className="shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}14` }}>
                <s.icon className="w-6 h-6" strokeWidth={1.5} style={{ color: s.color }} />
              </div>
              <div>
                <div className="text-[28px] lg:text-[32px] font-extrabold text-[#0B132B] leading-none tracking-tight">
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <div className="text-[12.5px] text-[#4B5563] mt-1 leading-tight font-medium">{s.label}</div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/about" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A5FAB] hover:gap-3 transition-all">
            See our impact and mission <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---------------- Community Story Moment ----------------
// NOTE (Phase 1 provenance): This section presents a single, general community
// note — NOT an unverified patient-attributed quote. It links out to /stories
// where verified stories will be published (currently an honest empty state).
function CommunityMoment() {
  return (
    <section className="bg-[#F8F9FB]">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="w-16 h-16 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0">
            <Quote className="w-6 h-6 text-[#23803F]" />
          </div>
          <div>
            <p className="btc-eyebrow mb-4">A COMMUNITY MOMENT</p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#0B132B] tracking-tight leading-tight mb-6">
              "No one should have to walk this alone."
            </h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-8 max-w-2xl">
              That single idea shaped everything about BTC — the community, the resources, the way
              we listen. It is the through-line that connects every patient, carer, volunteer and
              partner who walks with us.
            </p>
            <Link to="/stories" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A5FAB] hover:gap-3 transition-all">
              Read stories from the community <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------- Get Involved Teaser ----------------
function GetInvolvedTeaser() {
  const items = [
    { icon: HeartHandshake, title: "Become a Buddy", desc: "Survivors and carers can walk beside someone newly diagnosed.", color: "#2D9B4E", to: "/buddy/apply", tid: "landing-cta-buddy" },
    { icon: HandHeart, title: "Volunteer", desc: "Give an hour, a skill, or a warm welcome to our community.", color: "#1A5FAB", to: "/volunteer/apply", tid: "landing-cta-volunteer" },
    { icon: Building2, title: "Partner with BTC", desc: "Hospital trusts, charities and organisations working alongside us.", color: "#1F5D5B", to: "/organization/apply", tid: "landing-cta-partner" },
    { icon: Sparkles, title: "Donate", desc: "Keep BTC free for the people who need it most.", color: "#2D9B4E", to: "/donate", tid: "landing-cta-donate" },
  ];
  return (
    <section className="bg-white">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-28">
        <div className="max-w-2xl mb-12">
          <p className="btc-eyebrow mb-3">GET INVOLVED</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0B132B] tracking-tight leading-tight">
            BTC is powered by people.
          </h2>
          <p className="text-[#4B5563] mt-4 text-lg max-w-xl">
            There is more than one way to be part of BTC. Whichever path fits you, we would love
            to have you walking with us.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.05 }}
            >
              <Link to={it.to} data-testid={it.tid} className="block rounded-2xl bg-[#F8F9FB] border border-gray-100 p-6 hover:border-gray-300 hover:shadow-sm transition group h-full">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${it.color}14` }}>
                  <it.icon className="w-5 h-5" strokeWidth={1.5} style={{ color: it.color }} />
                </div>
                <h3 className="font-bold text-[16px] text-[#0B132B] mb-2 group-hover:text-black">{it.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{it.desc}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold" style={{ color: it.color }}>
                  Get started <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/get-involved" className="btc-btn-primary">
            Explore ways to help
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---------------- Final CTA ----------------
function FinalCTA() {
  return (
    <section
      data-testid={LANDING.finalCta}
      className="bg-[#0B132B] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30 bg-gradient-to-tr from-[#1F5D5B] via-transparent to-[#1A5FAB]" />
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#2D9B4E]/15 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#1A5FAB]/20 blur-3xl" />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12 py-24 lg:py-32 relative text-center">
        <p className="text-[#23803F] text-xs font-bold uppercase tracking-[0.22em] mb-6">
          JOIN THE MOVEMENT
        </p>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
          Join Bonded Thru Cancer.
          <br />
          <span className="text-[#23803F]">Walk with us.</span>
        </h2>
        <p className="text-white/75 text-lg max-w-2xl mx-auto mb-10">
          Whether you need support or want to give it, BTC is here — free, human and always
          ready to listen.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/support"
            data-testid={LANDING.finalCtaJoin}
            className="btc-btn-primary justify-center"
          >
            GET SUPPORT
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/portal-preview"
            data-testid={LANDING.finalCtaExplore}
            className="btc-btn-ghost-white justify-center"
          >
            EXPLORE THE PORTAL
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/60 text-xs">
          <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> NHS-aligned · GDPR compliant</div>
          <div className="flex items-center gap-2"><Activity className="w-4 h-4" /> Trusted by 320+ hospital trusts</div>
          <div className="hidden sm:flex items-center gap-2"><PhoneCall className="w-4 h-4" /> 24/7 Support Line</div>
        </div>
      </div>
    </section>
  );
}

// ---------------- Page ----------------
// Phase 1 homepage — ONE intentional scroll.
// HERO → WHAT BTC IS → HOW BTC HELPS → COMMUNITY STORY → GET INVOLVED TEASER → FINAL CTA → FOOTER
export default function Landing() {
  return (
    <div className="bg-white">
      <Header />
      <main id="main-content">
        <Hero />
        <WhatBTCIs />
        <HowBTCHelps />
        <CommunityMoment />
        <GetInvolvedTeaser />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
